import { Bug } from "lucide-react";
import { SeverityTag } from "@/components/ui/Badge";
import ScreenshotViewer from "./ScreenshotViewer";

// Structured bug report: title, severity, expected vs actual, repro steps,
// and an evidence screenshot. Shared by run detail + project results.
// Evidence: prefer the failed step's screenshot from the auth-protected
// endpoint (evidenceRunId + evidenceStepIndex); otherwise a direct path.

/**
 * The runners don't agree on what a bug report looks like.
 *
 *   cloud      → { title, severity, expected, actual, repro }
 *   exe        → { reason, failed_step: { action, success, detail } }
 *   extension  → no bug report at all; the narrative is the last step's `reason`
 *
 * This card was written against the cloud shape only, so an exe failure rendered
 * a card with an empty title and two blank rows labelled EXPECTED and ACTUAL —
 * the reader was shown a bug report that said nothing, which is worse than
 * showing none. Normalising here keeps that knowledge in one place instead of
 * spreading runner conditionals through every screen that renders a failure.
 *
 * `fallbackNarrative` carries the extension case: the caller passes the failing
 * step's reason, so the card still has something true to say.
 */
function normalise(bug, fallbackNarrative) {
  const failedStep = bug.failed_step || null;
  // The exe agent puts its whole account of the failure in `reason`, and repeats
  // it inside failed_step.detail. Either is the "actual"; prefer the longer one,
  // since the agent sometimes truncates the outer copy.
  const detail = String(failedStep?.detail || "").trim();
  const reason = String(bug.reason || "").trim();
  const narrative = String(bug.actual || "").trim()
    || (detail.length > reason.length ? detail : reason)
    || String(fallbackNarrative || "").trim();

  // A title, if none was given: the first sentence of the narrative reads as one
  // and beats a card headed by nothing.
  const firstSentence = narrative.split(/(?<=[.!?])\s/)[0] || "";
  const title = String(bug.title || "").trim()
    || (firstSentence.length > 110 ? `${firstSentence.slice(0, 110)}…` : firstSentence)
    || "This case failed";

  return {
    title,
    severity: bug.severity,
    expected: String(bug.expected || "").trim(),
    actual: narrative,
    // Which step gave way — exe names it, and it's the single most useful
    // orienting fact for someone about to go fix this.
    failedAction: String(failedStep?.action || "").trim(),
    repro: bug.repro || bug.steps_to_reproduce || [],
  };
}

export default function BugReportCard({
  bug, evidenceRunId, evidenceStepIndex, fallbackNarrative, kind = "cloud",
}) {
  // Nothing to say at all — no report AND no narrative to stand in for one.
  if (!bug && !fallbackNarrative) return null;
  const b = normalise(bug || {}, fallbackNarrative);
  const hasEndpointEvidence = evidenceRunId != null && evidenceStepIndex != null;
  const directEvidence = (bug || {}).evidence_screenshot || (bug || {}).evidence;

  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4 dark:border-rose-500/20 dark:bg-rose-500/10">
      <div className="flex items-start justify-between gap-2">
        <h4 className="flex items-start gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          <Bug size={16} className="mt-0.5 shrink-0 text-rose-500" /> {b.title}
        </h4>
        {b.severity && <SeverityTag severity={b.severity} />}
      </div>

      <dl className="mt-3 space-y-2 text-sm">
        {/* Each row appears only when it has content. A row labelled EXPECTED
            with nothing under it tells the reader less than no row at all. */}
        {b.expected && (
          <div>
            <dt className="text-xs font-semibold uppercase text-slate-400">Expected</dt>
            <dd className="text-slate-700 dark:text-slate-200">{b.expected}</dd>
          </div>
        )}
        {b.actual && (
          <div>
            <dt className="text-xs font-semibold uppercase text-slate-400">
              {b.expected ? "Actual" : "What happened"}
            </dt>
            <dd className="whitespace-pre-line text-slate-700 dark:text-slate-200">{b.actual}</dd>
          </div>
        )}
        {b.failedAction && (
          <div>
            <dt className="text-xs font-semibold uppercase text-slate-400">Failed at</dt>
            <dd className="font-mono text-xs text-slate-600 dark:text-slate-300">{b.failedAction}</dd>
          </div>
        )}
        {b.repro.length > 0 && (
          <div>
            <dt className="text-xs font-semibold uppercase text-slate-400">
              Steps to reproduce
            </dt>
            <dd>
              <ol className="mt-1 list-inside list-decimal space-y-0.5 text-slate-700 dark:text-slate-200">
                {b.repro.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ol>
            </dd>
          </div>
        )}
      </dl>

      {(hasEndpointEvidence || directEvidence) && (
        <div className="mt-3">
          <p className="mb-1.5 text-xs font-semibold uppercase text-slate-400">Evidence</p>
          {hasEndpointEvidence ? (
            <ScreenshotViewer runId={evidenceRunId} stepIndex={evidenceStepIndex} kind={kind} />
          ) : (
            <ScreenshotViewer src={directEvidence} />
          )}
        </div>
      )}
    </div>
  );
}
